import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

interface CookPayoutRow {
  id: number;
  paidAt: string | null;
  orderedAt: string;
  paidAmount: string | null;
  totalPrice: string;
  balanceAmount: string | null;
  paymentStatus: string;
  foodScheduleId: number;
  recordCount: any;
}
interface CookPayoutItem {
  id: number;
  paidAt: string | null;
  orderedAt: string;
  paidAmount: string | null;
  totalPrice: string;
  balanceAmount: string | null;
  paymentStatus: string;
  foodScheduleId: number;
}

interface WeeklyPeriod {
  items: CookPayoutItem[];
  period: string; // e.g. "2026-W04"
  periodEnd: string;
  periodStart: string;
  recordCount: number;
  totalAmount: number;
  paymentStatus: string;
}

interface MonthlyWeek {
  week: string;
  items: CookPayoutItem[];
  weekEnd: string;
  weekStart: string;
  recordCount: number;
  totalAmount: number;
  paymentStatus: string;
}

interface MonthlyGroup {
  month: string; // e.g. "2026-01"
  weeks: MonthlyWeek[];
  monthEnd: string;
  monthStart: string;
}

interface CookPayoutApiResponse {
  data: {
    data: WeeklyPeriod[] | MonthlyGroup[];
    count: number;
    groupBy: 'week' | 'month';
    db_status: string;
    cookProfileId?: number;
    weeks?: never;
    items?: never;
  };
  isSuccess: boolean;
  status: number;
  count?: number;
  cookProfileId?: number;
}
interface PayoutState {
  data: CookPayoutApiResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: PayoutState = {
  data: null,
  loading: false,
  error: null,
};

const MASTERPORT = import.meta.env.VITE_FOOD_ORDER_PORT;
const MASTERURL = import.meta.env.VITE_API_FOOD_ORDER_URL;

export const customSearchCookPayout = createAsyncThunk<
  CookPayoutApiResponse,
  { groupBy?: any; userProfileId?: any; period?: any },
  { rejectValue: string }
>('Payout/customSearchCookPayout', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiService.post<any>(
      'custom_search_cook_payout',
      payload,
      MASTERPORT,
      MASTERURL,
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred');
  }
});
export const updateCookPayout = createAsyncThunk<
  CookPayoutApiResponse,
  any,
  { rejectValue: string }
>('Payout/updateCookPayout', async (params, { rejectWithValue }) => {
  try {
    const response = await apiService.put<{ data: CookPayoutApiResponse }>(
      '/update_cook_payout',
      params,
      MASTERPORT,
      MASTERURL,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});
export const payoutSlice = createSlice({
  name: 'Payout',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(customSearchCookPayout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(customSearchCookPayout.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(customSearchCookPayout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch payouts';
      });
  },
});

export default payoutSlice.reducer;
